import { useTheme } from '@/context/ThemeContext';
import { F, SOURCES, ALL_PAID, fC, fN, r2, moTrend, RANGE_LABEL } from '@/dashboard/data';
import { SecHead, MetCard, CT, Dot, Tag } from '@/dashboard/ui';
import {
  AreaChart, Area, BarChart, Bar, ComposedChart, ScatterChart, Scatter, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Line
} from 'recharts';

/* ═══════════════════ OVERVIEW TAB ═══════════════════ */
export function OverviewTab({ data }) {
  const { colors: T } = useTheme();
  const k = data.kpis, mo = data.monthly;
  const isB2B = k.kind === 'b2b';
  // Real month-over-month delta, so the arrow never contradicts the chart
  // beneath it and never changes on a re-render.
  const rl = RANGE_LABEL[data.range] || data.range;
  const tr = key => {
    const cur = +mo[mo.length-1]?.[key] || 0, prev = +mo[mo.length-2]?.[key] || 0;
    return prev ? r2((cur - prev) / prev * 100) : 0;
  };
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <div>
        <SecHead title="Key Performance Indicators" accent="#00D4FF" right={`${SOURCES.filter(s=>s.ok).length} live`} />
        <div className="kpi-grid" data-testid="kpi-grid">
          {isB2B ? (
            <>
              <MetCard label="Pipeline" value={fC(k.pipelineValue)} sub={`Win rate ${k.winRate}%`} col="#4285F4" trend={tr("pipeline")} chartData={mo} dateRange={rl} chartKey="pipeline" valueFormat="currency" />
              <MetCard label="Total Spend" value={fC(k.totalSpend)} sub={`Leads ${fN(k.totalLeads)}`} col="#00D4FF" trend={tr("spend")} chartData={mo} dateRange={rl} chartKey="spend" valueFormat="currency" />
            </>
          ) : (
            <>
              <MetCard label="Total GMV" value={fC(k.totalGMV)} sub={`Spend ${fC(k.totalSpend)}`} col="#00D4FF" trend={tr("gmv")} chartData={mo} dateRange={rl} chartKey="gmv" valueFormat="currency" />
              <MetCard label="Blended ROAS" value={`${k.blendedROAS}x`} sub={`ACOS ${k.blendedACOS}%`} col="#06D6A0" trend={tr("roas")} chartData={mo} dateRange={rl} chartKey="roas" valueFormat="ratio" />
            </>
          )}
          <MetCard label="Blended ROI" value={`${k.blendedROI}%`} sub={`Gross margin ${k.grossMargin}%`} col="#FFD166" trend={tr("roi")} chartData={mo} dateRange={rl} chartKey="roi" valueFormat="pct" />
          <MetCard label={isB2B ? 'Closed Won' : 'Total Orders'} value={fN(k.totalOrders)} sub={isB2B ? `ACV ${fC(k.aov)}` : `AOV ${fC(k.aov)}`} col="#C77DFF" trend={tr("orders")} chartData={mo} dateRange={rl} chartKey="orders" valueFormat="count" />
          <MetCard label="Avg SOV" value={`${k.avgSOV}%`} sub={`NTB ${k.totalNTB}%`} col="#FF6B6B" trend={tr("sov")} chartData={mo} dateRange={rl} chartKey="sov" valueFormat="pct" />
          <MetCard label="LTV : CAC" value={`${k.ltvCac}x`} sub={`CAC ${fC(k.cac)}`} col={k.ltvCac>=3?'#06D6A0':'#FFD166'} trend={tr("ltvCac")} chartData={mo} dateRange={rl} chartKey="ltvCac" valueFormat="ratio" />
          {!isB2B && <MetCard label="Avg Order Value" value={fC(k.aov)} sub={`${fN(k.newCustomers)} new customers`} col="#4285F4" trend={tr("aov")} chartData={mo} dateRange={rl} chartKey="aov" valueFormat="currency" />}
          {isB2B && <MetCard label="Avg Deal Size" value={fC(k.aov)} sub={`Sales cycle ${data.crm.salesCycle}d`} col="#4285F4" trend={tr("aov")} chartData={mo} dateRange={rl} chartKey="aov" valueFormat="currency" />}
          <MetCard label="Revenue Leakage" value={fC(k.revenueLeakage)} sub={`${data.blindSpots.length} open blind spots`} col="#FF4444" trend={tr("leakage")} chartData={mo} dateRange={rl} chartKey="leakage" valueFormat="currency" />
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:14 }}>
        <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
          <SecHead title={isB2B ? 'Revenue & Ad Spend Trend' : 'GMV & Ad Spend Trend'} accent="#00D4FF" />
          <ResponsiveContainer width="100%" height={isMobile?160:200}>
            <AreaChart data={mo}>
              <defs>
                <linearGradient id="gGMV" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00D4FF" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#00D4FF" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine}/>
              <XAxis dataKey="month" tick={{fill:T.axisTick,fontSize:10,fontFamily:F.mono}}/>
              <YAxis tick={{fill:T.axisTick,fontSize:10,fontFamily:F.mono}} tickFormatter={fC}/>
              <Tooltip content={<CT/>}/>
              <Area type="monotone" dataKey="gmv" name="GMV" stroke="#00D4FF" strokeWidth={2} fill="url(#gGMV)"/>
              <Area type="monotone" dataKey="spend" name="Spend" stroke="#FF6B6B" strokeWidth={1.5} fill="transparent" strokeDasharray="5 3"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
          <SecHead title="Revenue & Conversions" accent="#06D6A0" />
          <ResponsiveContainer width="100%" height={isMobile?160:200}>
            <ComposedChart data={mo}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine}/>
              <XAxis dataKey="month" tick={{fill:T.axisTick,fontSize:10,fontFamily:F.mono}}/>
              <YAxis yAxisId="l" tick={{fill:T.axisTick,fontSize:10,fontFamily:F.mono}} tickFormatter={fC}/>
              <YAxis yAxisId="r" orientation="right" tick={{fill:T.axisTick,fontSize:10,fontFamily:F.mono}}/>
              <Tooltip content={<CT/>}/>
              <Bar yAxisId="l" dataKey="revenue" name="Revenue" fill="rgba(6,214,160,.3)" stroke="#06D6A0" strokeWidth={1}/>
              <Line yAxisId="r" type="monotone" dataKey="conversions" name="Conversions" stroke="#FFD166" strokeWidth={2} dot={false}/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Strong Spots Preview */}
      <div>
        <SecHead title="Strong Spots" accent="#06D6A0" right={`${data.strongSpots.length} active`} />
        <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)', gap:8 }}>
          {data.strongSpots.slice(0,3).map(s => (
            <div key={s.id} style={{ background:'rgba(6,214,160,.04)', border:'1px solid rgba(6,214,160,.1)',
              borderLeft:`3px solid ${s.col}`, borderRadius:'0 8px 8px 0', padding:'12px 14px' }}>
              <div style={{ fontSize:14, fontWeight:700, color:T.cardText, fontFamily:F.head, marginBottom:4 }}>{s.title}</div>
              <div style={{ display:'flex', gap:6, marginBottom:6 }}>
                <Tag col={s.col}>{s.ch}</Tag>
                <span style={{ fontSize:13, color:'#06D6A0', fontFamily:F.mono, fontWeight:600 }}>{s.metric}</span>
              </div>
              <div style={{ fontSize:12, color:T.textSecondary, lineHeight:1.5 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Blind Spots Preview */}
      <div>
        <SecHead title="Critical Blind Spots" accent="#FF4444" right={`${fC(data.blindSpots.reduce((a,b)=>a+b.impact,0))} at risk`} />
        <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(2,1fr)', gap:8 }}>
          {data.blindSpots.filter(b=>b.sev==='critical').map(b => {
            const sc = '#FF4444';
            return (
              <div key={b.id} style={{ background:`${sc}08`, border:`1px solid ${sc}22`,
                borderLeft:`3px solid ${sc}`, borderRadius:'0 8px 8px 0', padding:'12px 14px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:T.cardText, fontFamily:F.head, maxWidth:'65%' }}>{b.title}</div>
                  <div style={{ fontSize:14, color:'#06D6A0', fontFamily:F.mono, fontWeight:700 }}>+{fC(b.impact)}<span style={{fontSize:10,color:T.dim}}>/mo</span></div>
                </div>
                <div style={{ display:'flex', gap:5, marginBottom:6 }}><Tag col={sc}>critical</Tag><Tag col={T.muted}>{b.ch}</Tag></div>
                <div style={{ fontSize:12, color:T.textSecondary, lineHeight:1.5 }}>{b.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════ PAID MEDIA TAB ═══════════════════ */
export function PaidTab({ data }) {
  const { colors: T } = useTheme();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const paid = data.paid.filter(p => p.connected !== false);
  const offline = data.paid.filter(p => p.connected === false);
  const totalSpend = paid.reduce((a,p)=>a+p.spend,0);
  const totalRev = paid.reduce((a,p)=>a+p.revenue,0);
  const rl = RANGE_LABEL[data.range] || data.range;
  const tr = key => moTrend(data.monthly, key);
  const revLabel = data.kpis?.kind === 'b2b' ? 'Marketing Revenue' : 'Total GMV';

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <div style={{ display:'grid', gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(auto-fill,minmax(168px,1fr))', gap:8 }}>
        <MetCard label="Total Spend" value={fC(totalSpend)} col="#FF6B6B" trend={tr('spend')} chartData={data.monthly} chartKey="spend" valueFormat="currency" dateRange={rl} />
        <MetCard label={revLabel} value={fC(totalRev)} col="#06D6A0" trend={tr('gmv')} chartData={data.monthly} chartKey="gmv" valueFormat="currency" dateRange={rl} />
        <MetCard label="Blended ROAS" value={`${r2(totalRev/totalSpend)}x`} col="#00D4FF" trend={tr('roas')} chartData={data.monthly} chartKey="roas" valueFormat="ratio" dateRange={rl} />
        <MetCard label="Platforms Active" value={`${paid.length}`} sub={`${ALL_PAID.length} supported \u00B7 ${offline.length} not connected`} col="#FFD166" />
      </div>

      <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:8, padding:isMobile?12:18, overflowX:'auto' }}>
        <SecHead title="Platform Performance" accent="#4285F4" right={`${paid.length} platforms`} />
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, fontFamily:F.mono, minWidth:900 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${T.border}` }}>
              {['Platform','Spend','Revenue','ROAS','ACOS','Impr.','Clicks','Conv.','CTR','CVR','CPA','SOV','NTB%','ImpSh','Trend'].map(h =>
                <th key={h} style={{ padding:'8px 8px', color:T.textSecondary, textAlign:'left', fontSize:10, textTransform:'uppercase', whiteSpace:'nowrap', fontWeight:600 }}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {paid.map((p, i) => (
              <tr key={i} style={{ borderBottom:`1px solid ${T.border}` }}
                onMouseEnter={e => e.currentTarget.style.background=T.hoverBg}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <td style={{ padding:'8px', display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap' }}>
                  <span style={{ width:6, height:6, borderRadius:'50%', background:p.col, flexShrink:0 }} />
                  <span style={{ color:T.text, fontWeight:500 }}>{p.name}</span>
                  <Tag col={p.col}>{p.cat}</Tag>
                </td>
                <td style={{ padding:'8px', color:T.textSecondary }}>{fC(p.spend)}</td>
                <td style={{ padding:'8px', color:T.text, fontWeight:600 }}>{fC(p.revenue)}</td>
                <td style={{ padding:'8px', color:p.roas>=3?'#06D6A0':p.roas>=2?'#FFD166':'#FF4444', fontWeight:700 }}>{p.roas}x</td>
                <td style={{ padding:'8px', color:p.acos<=20?'#06D6A0':'#FFD166' }}>{p.acos}%</td>
                <td style={{ padding:'8px', color:T.muted }}>{fN(p.impressions)}</td>
                <td style={{ padding:'8px', color:T.textSecondary }}>{fN(p.clicks)}</td>
                <td style={{ padding:'8px', color:T.text }}>{fN(p.conversions)}</td>
                <td style={{ padding:'8px', color:T.muted }}>{p.ctr}%</td>
                <td style={{ padding:'8px', color:T.muted }}>{p.cvr}%</td>
                <td style={{ padding:'8px', color:T.muted }}>{fC(p.cpa)}</td>
                <td style={{ padding:'8px', color:T.textSecondary }}>{p.sov}%</td>
                <td style={{ padding:'8px', color:T.muted }}>{p.ntb}%</td>
                <td style={{ padding:'8px', color:T.muted }}>{p.impressionShare}%</td>
                <td style={{ padding:'8px', color:p.trend>=0?'#06D6A0':'#FF4444', fontWeight:500 }}>
                  {p.trend>=0?'\u25B2':'\u25BC'}{Math.abs(p.trend)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:14 }}>
        <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
          <SecHead title="Spend by Platform" accent="#FF6B6B" />
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={paid.slice(0,8)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine} horizontal={false}/>
              <XAxis type="number" tick={{fill:T.axisTick,fontSize:11,fontFamily:F.mono}} tickFormatter={fC}/>
              <YAxis type="category" dataKey="name" tick={{fill:T.axisTick,fontSize:11,fontFamily:F.mono}} width={100}/>
              <Tooltip content={<CT/>} formatter={v=>fC(v)}/>
              <Bar dataKey="spend" name="Spend" fill="#FF6B6B" opacity={0.8}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
          <SecHead title="ROAS by Platform" accent="#06D6A0" />
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={paid.slice(0,8)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine} horizontal={false}/>
              <XAxis type="number" tick={{fill:T.axisTick,fontSize:11,fontFamily:F.mono}}/>
              <YAxis type="category" dataKey="name" tick={{fill:T.axisTick,fontSize:11,fontFamily:F.mono}} width={100}/>
              <Tooltip content={<CT/>}/>
              <Bar dataKey="roas" name="ROAS">{paid.slice(0,8).map((p,i)=><Cell key={i} fill={p.roas>=3?'#06D6A0':p.roas>=2?'#FFD166':'#FF4444'}/>)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════ ECOM TAB ═══════════════════ */
export function EcomTab({ data }) {
  const { colors: T } = useTheme();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const ecom = data.ecom;
  const totalGMV = ecom.reduce((a,p)=>a+p.gmv,0);
  const totalOrders = ecom.reduce((a,p)=>a+p.orders,0);
  const types = ['marketplace','qcommerce','d2c'];
  const rl = RANGE_LABEL[data.range] || data.range;
  const tr = key => moTrend(data.monthly, key);

  if (!ecom.length) return (
    <div data-testid="ecom-empty-state" style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`,
      borderRadius:10, padding:'48px 32px', textAlign:'center', boxShadow:T.elev1 }}>
      <div style={{ fontSize:18, fontWeight:700, color:T.cardText, fontFamily:F.head, marginBottom:8 }}>
        No commerce data for {data.brand?.name}
      </div>
      <div style={{ fontSize:14, color:T.muted, maxWidth:460, margin:'0 auto', lineHeight:1.6 }}>
        This is a B2B account, so marketplace and quick-commerce metrics do not apply.
        Pipeline and deal performance live on the CRM &amp; Pipeline tab.
      </div>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <div style={{ display:'grid', gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(auto-fill,minmax(168px,1fr))', gap:8 }}>
        <MetCard label="Total GMV" value={fC(totalGMV)} col="#00D4FF" trend={tr('gmv')} chartData={data.monthly} chartKey="gmv" valueFormat="currency" dateRange={rl} />
        <MetCard label="Total Orders" value={fN(totalOrders)} col="#C77DFF" trend={tr('orders')} chartData={data.monthly} chartKey="orders" valueFormat="count" dateRange={rl} />
        <MetCard label="Avg Order Value" value={fC(r2(totalGMV/totalOrders))} col="#FFD166" trend={tr('aov')} chartData={data.monthly} chartKey="aov" valueFormat="currency" dateRange={rl} />
        <MetCard label="Platforms" value={`${ecom.length}`} col="#06D6A0" />
      </div>

      {types.map(type => {
        const plat = ecom.filter(p => p.type === type);
        if (!plat.length) return null;
        const typeLabel = {marketplace:'Marketplace',qcommerce:'Q-Commerce',d2c:'D2C'}[type];
        const typeCol = {marketplace:'#FF9900',qcommerce:'#8B5CF6',d2c:'#00D4FF'}[type];
        return (
          <div key={type}>
            <SecHead title={`${typeLabel} Platforms`} accent={typeCol} right={`${plat.length} active`} />
            <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:8, padding:isMobile?12:18, overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, fontFamily:F.mono, minWidth:800 }}>
                <thead>
                  <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                    {['Platform','GMV','Orders','AOV','CVR','SOV%','Shelf%','Vis.','BSR','Rating','NTB%','Repeat%','Return%','InvDays','Trend'].map(h =>
                      <th key={h} style={{ padding:'8px 8px', color:T.textSecondary, textAlign:'left', fontSize:10, textTransform:'uppercase', whiteSpace:'nowrap', fontWeight:600 }}>{h}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {plat.map((p,i) => (
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}` }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.hoverBg}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{ padding:'8px', display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap' }}>
                        <span style={{ width:6, height:6, borderRadius:'50%', background:p.col, flexShrink:0 }} />
                        <span style={{ color:T.text, fontWeight:500 }}>{p.name}</span>
                      </td>
                      <td style={{ padding:'8px', color:T.text, fontWeight:600 }}>{fC(p.gmv)}</td>
                      <td style={{ padding:'8px', color:T.textSecondary }}>{fN(p.orders)}</td>
                      <td style={{ padding:'8px', color:T.muted }}>{fC(p.aov)}</td>
                      <td style={{ padding:'8px', color:T.muted }}>{p.cvr}%</td>
                      <td style={{ padding:'8px', color:T.textSecondary }}>{p.sov}%</td>
                      <td style={{ padding:'8px', color:T.muted }}>{p.shareOfShelf}%</td>
                      <td style={{ padding:'8px', color:p.visibility>=70?'#06D6A0':'#FFD166' }}>{p.visibility}%</td>
                      <td style={{ padding:'8px', color:p.bsr<=10?'#06D6A0':'#FFD166' }}>#{p.bsr}</td>
                      <td style={{ padding:'8px', color:p.avgRating>=4.5?'#06D6A0':'#FFD166' }}>{p.avgRating}</td>
                      <td style={{ padding:'8px', color:T.muted }}>{p.ntb}%</td>
                      <td style={{ padding:'8px', color:T.muted }}>{p.repeatRate}%</td>
                      <td style={{ padding:'8px', color:p.returnRate>15?'#FF4444':'#06D6A0' }}>{p.returnRate}%</td>
                      <td style={{ padding:'8px', color:p.inventoryCoverage<30?'#FF4444':'#06D6A0' }}>{p.inventoryCoverage}d</td>
                      <td style={{ padding:'8px', color:p.trend>=0?'#06D6A0':'#FF4444' }}>
                        {p.trend>=0?'\u25B2':'\u25BC'}{Math.abs(p.trend)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {type === 'marketplace' && (
              <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:14, marginTop:14 }}>
                <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
                  <SecHead title="GMV by Platform" accent={typeCol} />
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={plat}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine}/>
                      <XAxis dataKey="name" tick={{fill:T.axisTick,fontSize:11,fontFamily:F.mono}}/>
                      <YAxis tick={{fill:T.axisTick,fontSize:11,fontFamily:F.mono}} tickFormatter={fC}/>
                      <Tooltip content={<CT/>} formatter={v=>fC(v)}/>
                      <Bar dataKey="gmv" name="GMV">{plat.map((p,i)=><Cell key={i} fill={p.col}/>)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
                  <SecHead title="SOV vs Share of Shelf" accent="#C77DFF" />
                  <ResponsiveContainer width="100%" height={180}>
                    <ScatterChart>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine}/>
                      <XAxis dataKey="sov" name="SOV %" tick={{fill:T.axisTick,fontSize:11,fontFamily:F.mono}}/>
                      <YAxis dataKey="shareOfShelf" name="Shelf %" tick={{fill:T.axisTick,fontSize:11,fontFamily:F.mono}}/>
                      <Tooltip content={({active,payload}) =>
                        active && payload?.length ? (
                          <div style={{ background:T.tooltipBg, border:`1px solid ${T.tooltipBorder}`, borderRadius:6, padding:'8px 12px', fontSize:10, fontFamily:F.mono }}>
                            <div style={{ color:T.text, marginBottom:4 }}>{payload[0]?.payload?.name}</div>
                            <div style={{ color:'#C77DFF' }}>SOV: {payload[0]?.payload?.sov}%</div>
                            <div style={{ color:'#FFD166' }}>Shelf: {payload[0]?.payload?.shareOfShelf}%</div>
                          </div>
                        ) : null
                      }/>
                      <Scatter data={plat}>{plat.map((p,i)=><Cell key={i} fill={p.col}/>)}</Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
